import type { Nullable } from "models-webgl2";
import { Vec2 } from "utils/math/vec2";
import { Webgl2dRenderer } from "../../../webgl2d-renderer";
import { Webgl2 } from "../../../webgl2";
import { Webgl2Component } from "../../webgl2-component";
import { ImageRectComponent } from "../image-rect-component";
import { ImageAdjustRectComponent } from "../image-adjust-rect-component";
import { CropSelectionComponent } from "./crop-selection-component";
import { CropUnitComponent } from "./crop-unit-component";
import { Mat3 } from "utils/math/mat3";
import { Transform } from "../../transform";

export class CropSelectionBottomLeftComponent extends Webgl2Component {
  private _tm3: Mat3;
  private _cropTopRight: Vec2;
  private _prevTransform: Transform;

  private get _selectedComponent(): Nullable<ImageRectComponent | ImageAdjustRectComponent> {
    const component = this._renderer.selectedComponent;
    return component instanceof ImageRectComponent || component instanceof ImageAdjustRectComponent
      ? component
      : null;
  }

  constructor(
    private readonly _renderer: Webgl2dRenderer,
    private readonly _cropSelectionComponent: CropSelectionComponent,
  ) {
    super();

    const topUnit = new CropUnitComponent(new Vec2(18, 4));
    topUnit.style.top = "100%";
    topUnit.style.translateY = "-100%";
    topUnit.style.pointerEvents = "none";
    this.appendChild(topUnit);

    const leftUnit = new CropUnitComponent(new Vec2(4, 18));
    leftUnit.style.pointerEvents = "none";
    this.appendChild(leftUnit);

    this.transform.setSize(18, 18);
  }

  public onMouseDown() {
    if (!this._selectedComponent) return;

    const selectedTopRight = Webgl2dRenderer.global.getComponentClipSpaceInGlobal(
      this._selectedComponent,
      Vec2.oneZero(),
    );
    const cropTopRight = Webgl2dRenderer.global.getComponentClipSpaceInGlobal(
      this._cropSelectionComponent,
      Vec2.oneZero(),
    );
    this._tm3 = Mat3.identity()
      .flipX()
      .rotate(-this._selectedComponent.transform.rads)
      .translate(selectedTopRight.clone().negate());
    this._cropTopRight = cropTopRight.transformMat3(this._tm3.mat);
    this._prevTransform = this._cropSelectionComponent.transform.clone();
  }

  public onMouseMove(e: MouseEvent | TouchEvent) {
    if (!this._selectedComponent) return;

    const currentMousePos = this._renderer.camera
      .getGlobalMousePixelPosition(e)
      .transformMat3(this._tm3.mat)
      .clamp(this._cropTopRight.clone().add(18, 18), new Vec2(Infinity, Infinity));

    const ratio =
      currentMousePos.distanceTo(this._cropTopRight) / this._prevTransform.size.length();
    const newSize = this._prevTransform.size.clone().multiply(ratio);
    const newBottomLeft = this._cropTopRight.clone().add(newSize);

    this._cropSelectionComponent.transform.calcDimensions({
      size: newSize,
      position: newBottomLeft
        .median(this._cropTopRight)
        .transformMat3(this._tm3.clone().invert().mat),
    });
    this._renderer.render();
  }

  public getRenderData() {
    const material = Webgl2.materials.transparent;
    return { material };
  }
}

export default CropSelectionBottomLeftComponent;
